import { Button, CircularProgress } from "@mui/material";
import { toast } from "sonner";

import {
	type QuestionnairePayload,
	useInvestmentQuestionnaireSdk,
} from "AdvisoryManagement/Hooks/useInvestmentQuestionnaireSdk";

type InvestmentQuestionnaireButtonProps = {
	clientData: QuestionnairePayload;
	onQuestionnaireCreated?: (data: {
		uuid: string;
		pdfPreviewLink?: string;
		pdfDownloadLink?: string;
	}) => void;
	variant?: "text" | "contained" | "outlined";
	size?: "small" | "medium" | "large";
	fullWidth?: boolean;
	children?: React.ReactNode;
};

export const InvestmentQuestionnaireButton = ({
	clientData,
	onQuestionnaireCreated,
	variant = "text",
	size = "small",
	fullWidth = false,
	children = "Vyplnit",
}: InvestmentQuestionnaireButtonProps) => {
	const { isConfigured, isLoaded, isLoading, openQuestionnaire } =
		useInvestmentQuestionnaireSdk({
			onQuestionnaireCreated: (data) => {
				toast.success("Investiční dotazník byl úspěšně vytvořen");
				onQuestionnaireCreated?.(data);
			},
			onError: (error) => {
				console.error("Investment questionnaire SDK error:", error);
				// Don't show toast if it's just a configuration error
				if (!error.message.includes("not configured")) {
					toast.error(
						"Nastala chyba při načítání investičního dotazníku. Zkuste to prosím znovu.",
					);
				}
			},
		});

	const handleClick = () => {
		if (!isConfigured) {
			toast.error(
				"Investiční dotazník není nakonfigurován. Kontaktujte správce systému.",
			);
			return;
		}

		if (!isLoaded) {
			toast.error("SDK investičního dotazníku se načítá, zkuste to za chvíli.");
			return;
		}

		openQuestionnaire(clientData);
	};

	return (
		<Button
			variant={variant}
			size={size}
			color="primary"
			onClick={handleClick}
			disabled={!isConfigured || isLoading}
			fullWidth={fullWidth}
			sx={{
				textTransform: "none",
				fontWeight: 500,
				minWidth: "auto",
				px: 1,
			}}
			title={
				!isConfigured
					? "Investiční dotazník není nakonfigurován"
					: isLoading
						? "Načítání..."
						: undefined
			}
		>
			{isLoading ? <CircularProgress size={16} /> : children}
		</Button>
	);
};
